import { Box, Group } from '@mantine/core';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import SingleSelector from '@repo/common/components/SingleSelector/SingleSelector';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { Button, Icon, Text } from '@repo/foundations';
import type { ReactNode } from 'react';
import React from 'react';
import { useUserGroupList } from '../../../../api';

interface TeamAndGroupSelectorProps {
	onTeamSelected: (teamId: string) => void;
	onGroupSelected: (groupId: string) => void;
	includeGroups?: boolean;
	value?: string;
}

export function TeamAndGroupSelector({
	onTeamSelected,
	onGroupSelected,
	includeGroups = true,
	value,
}: TeamAndGroupSelectorProps) {
	const { data: teams } = useApiListTeamsV2({
		queryParams: {
			only_write: false,
		},
	});

	const { data: groups } = useUserGroupList({
		options: {
			select: (res) => res.results,
		},
	});

	const [selected, setSelected] = React.useState<
		SelectablePropertyItem | undefined
	>();

	const options: SelectablePropertyItem[] = React.useMemo(
		() => [
			...(teams?.map((t) => ({
				label: t.name,
				value: t.id,
				icon: t.icon,
				group: 'teams',
			})) ?? []),
			...(!includeGroups
				? []
				: (groups?.map((g) => ({
						label: g.name,
						value: g.id,
						icon: g.icon,
						group: 'groups',
					})) ?? [])),
		],
		[groups, includeGroups, teams]
	);

	React.useEffect(() => {
		if (!value) {
			if (selected) {
				setSelected(undefined);
			}
			return;
		}

		const selectedOption = options.find((option) => option.value === value);
		if (!selectedOption) {
			if (selected) {
				setSelected(undefined);
			}
			return;
		}

		if (selectedOption.value !== selected?.value) {
			setSelected(selectedOption);
		}
	}, [options, selected?.value, value]);

	const handleChange = (value: string) => {
		const selectedOption = options.find((o) => o.value === value);
		if (selectedOption) {
			setSelected(selectedOption);
		}

		if (selectedOption?.group === 'teams') {
			onTeamSelected(value);
			onGroupSelected('');
		} else {
			onGroupSelected(value);
			onTeamSelected('');
		}
	};

	return (
		<Box maw={512}>
			<SingleSelector
				iconType="emoji"
				isViewerUser={false}
				searchable
				options={options}
				placeholder="All teams"
				placeholderIconName="users"
				initialSelected={value}
				onChange={handleChange}
				property="team"
				target={
					<Button size="md">
						<Group spacing="3xs" noWrap>
							{selected ? (
								<Text>{selected?.icon || ('💼' as ReactNode)}</Text>
							) : (
								<Icon name="users" />
							)}
							<Text size="sm">{selected?.label || 'All teams'}</Text>
						</Group>
					</Button>
				}
			/>
		</Box>
	);
}
